import { useMemo, useState } from 'react';
import type { ColumnDef } from '@vnetwork/ui/data';
import { DataTable, EmptyState } from '@vnetwork/ui/data';
import { Search } from 'lucide-react';
import { Toolbar } from '@vnetwork/ui/layout';
import { Button, Input, Skeleton } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';

interface CloudTableSectionProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Shown as EmptyState title when the (successful) result is empty. */
  emptyTitle: string;
  emptyDescription?: string;
  getRowId?: (row: T, index: number) => string;
  onRowClick?: (row: T) => void;
  /**
   * Enables a search `Toolbar` above the table (ListPageRecipe pattern). Returns
   * the searchable text for a row; the query filters case-insensitively.
   */
  searchText?: (row: T) => string;
  searchPlaceholder?: string;
}

/**
 * One cloud drill-down tab body, built to the app's canonical list pattern:
 * optional search `Toolbar` → skeleton rows / `Alert` (retry) / bare `DataTable`
 * — the SAME surface as the Processes/Inventory lists (the DataTable owns its own
 * border, so it is NOT wrapped in a Card). ServiceMgmt endpoints are backend-flaky
 * on staging, so an error degrades to a retryable Alert rather than crashing.
 */
export function CloudTableSection<T>({
  columns,
  data,
  isLoading,
  isError,
  onRetry,
  emptyTitle,
  emptyDescription,
  getRowId,
  onRowClick,
  searchText,
  searchPlaceholder,
}: CloudTableSectionProps<T>) {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const all = data ?? [];
    const q = query.trim().toLowerCase();
    if (!q || !searchText) return all;
    return all.filter((r) => searchText(r).toLowerCase().includes(q));
  }, [data, query, searchText]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {searchText ? (
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder ?? 'Search…'}
              startSlot={<Search />}
              style={{ maxWidth: 320 }}
            />
          }
        />
      ) : null}

      {isError ? (
        <Alert
          tone="danger"
          title={`${emptyTitle} — service unavailable`}
          actions={
            <Button size="sm" variant="ghost" onClick={onRetry}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 24 }} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={getRowId}
          onRowClick={onRowClick}
          emptyState={<EmptyState size="sm" title={emptyTitle} description={emptyDescription} />}
        />
      )}
    </div>
  );
}
